import React from 'react';
import { Link } from 'react-router-dom';
import { LUX_CSS, useLuxMotion } from './LuxShared';

const INDUSTRIES = [
  {
    key: 'healthcare',
    name: 'Healthcare',
    img: '/nh-media/ind-healthcare.jpg',
    short: 'Value-based care, delivered through technology.',
    body: "In the Healthcare sector, Firebucks has significantly improved patient care through innovative technology solutions. Our focus on value-based care has revolutionized healthcare delivery, aligning provider payments with patient outcomes. By implementing advanced analytics and care coordination platforms, we've helped healthcare organizations transition from fee-for-service to value-based models, resulting in improved quality of care and reduced costs.",
  },
  {
    key: 'fintech',
    name: 'FinTech',
    img: '/nh-media/ind-fintech.jpg',
    short: 'Secure digital banking and AI-driven investment tooling.',
    body: "Firebucks leverages cutting-edge technology to transform the FinTech industry, with a focus on banking and investment solutions. Our innovative platforms have revolutionized digital banking, providing seamless and secure transactions for millions of users worldwide. In the investment sector, we've developed AI-driven analytics tools that empower financial advisors and individual investors to make data-informed decisions, ultimately leading to improved portfolio performance and risk management.",
  },
  {
    key: 'media',
    name: 'Media & Entertainment',
    img: '/nh-media/ind-media.jpg',
    short: 'Immersive content, delivered flawlessly at scale.',
    body: 'In the Media and Entertainment industry, Firebucks has enabled companies to deliver immersive and engaging content across every screen — with streaming performance, content pipelines and playback quality engineered to hold up under real audience load.',
  },
  {
    key: 'retail',
    name: 'Retail',
    img: '/nh-media/ind-retail.jpg',
    short: 'Commerce experiences that convert, supply chains that hold.',
    body: "Firebucks' expertise in the Retail industry focuses on enhancing customer experiences and optimizing supply chain management. Our e-commerce solutions have revolutionized online shopping, providing seamless, personalized experiences that drive conversion rates and customer loyalty. In automotive retail, we've developed innovative digital showrooms and virtual test drive platforms, transforming the car buying process and helping dealerships adapt to changing consumer preferences in the digital age.",
  },
  {
    key: 'elearning',
    name: 'eLearning',
    img: '/nh-media/ind-elearning.jpg',
    short: 'Adaptive learning platforms that actually hold attention.',
    body: 'In the eLearning sector, Firebucks provides innovative solutions that enhance the learning experience and improve educational outcomes. Our adaptive learning platforms use AI to personalize content delivery, while our virtual classroom technologies enable seamless remote education, fostering engagement and knowledge retention for students of all ages.',
  },
  {
    key: 'gaming',
    name: 'Gaming',
    img: '/nh-media/ind-gaming.jpg',
    short: 'Ship bug-free across every platform and device.',
    body: "Firebucks' work in the Gaming industry is highlighted by our comprehensive game testing and quality assurance services. Our expert testers ensure seamless gameplay experiences across multiple platforms and devices, identifying and resolving issues before they reach end-users. We provide functional testing, localization testing, and compliance checks, helping game developers deliver high-quality, bug-free products that meet global standards and player expectations.",
  },
  {
    key: 'travel',
    name: 'Travel, Tourism & Hospitality',
    img: '/nh-media/ind-travel.jpg',
    short: 'Frictionless booking, protected customer data.',
    body: 'In the Travel, Tourism & Hospitality sector, Firebucks has enabled companies to enhance their service delivery and customer engagement. Our innovative solutions focus on improving user experiences through personalized recommendations and seamless booking processes. We also prioritize data protection, implementing robust security measures to safeguard sensitive customer information and ensure compliance with global privacy regulations.',
  },
];

const IndustriesLux = () => {
  const rootRef = useLuxMotion();

  return (
    <div className="fx-root" ref={rootRef}>
      <style>{LUX_CSS}</style>
      <style>{`
        .ix-grid { display:grid; grid-template-columns:repeat(3,1fr); gap:24px; }
        .ix-card { position:relative; background:#fff; border:1px solid var(--fx-line); border-radius:20px; overflow:hidden;
          box-shadow:0 18px 44px -28px rgba(19,26,38,.3); transition:transform .35s cubic-bezier(.16,.84,.32,1), box-shadow .35s, border-color .3s; }
        .ix-card:hover { transform:translateY(-6px); box-shadow:0 30px 66px -28px rgba(19,26,38,.42); border-color:var(--fx-fire); }
        .ix-media { position:relative; height:150px; overflow:hidden; background:#eef2f7; }
        .ix-media img { width:100%; height:100%; object-fit:cover; transition:transform .6s cubic-bezier(.16,.84,.32,1); }
        .ix-card:hover .ix-media img { transform:scale(1.07); }
        .ix-media::after { content:''; position:absolute; inset:0; background:linear-gradient(180deg,transparent 45%,rgba(255,255,255,.92)); }
        .ix-body { padding:20px 24px 24px; }
        .ix-body h3 { font-size:19px; font-weight:800; color:var(--fx-ink); margin-bottom:8px; letter-spacing:-.01em; }
        .ix-lead { font-size:14px; color:var(--fx-body); line-height:1.6; font-weight:600; }
        .ix-desc { padding-top:14px; margin-top:14px; border-top:1px solid var(--fx-line); font-size:14px; line-height:1.7; color:var(--fx-body); }
        @media (max-width:980px) { .ix-grid { grid-template-columns:1fr; } }
      `}</style>

      <section className="fx-hero">
        <video className="fx-hero-video" muted loop playsInline autoPlay preload="auto" src="/nh-media/industries-tech.mp4" />
        <div className="fx-hero-scrim" />
        <div className="fx-hero-inner">
          <h1 className="fx-reveal">Unleashing potential across <span className="fx-accent">industry verticals</span></h1>
          <p className="fx-reveal">The engineering bar is the same everywhere. What changes is the regulation, the risk and the user — and we've shipped in all of them.</p>
        </div>
      </section>

      <section className="fx-section">
        <div className="fx-wrap">
          <div className="fx-head fx-reveal">
            <h2>Seven verticals. <span className="fx-accent">One standard of delivery.</span></h2>
            <p>How the same engineering standard plays out, vertical by vertical.</p>
          </div>
          <div className="ix-grid">
            {INDUSTRIES.map((ind) => (
              <article key={ind.key} className="ix-card fx-reveal">
                <div className="ix-media"><img src={ind.img} alt={ind.name} loading="lazy" /></div>
                <div className="ix-body">
                  <h3>{ind.name}</h3>
                  <p className="ix-lead">{ind.short}</p>
                  <p className="ix-desc">{ind.body}</p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="fx-cta">
        <video className="fx-cta-video" muted loop playsInline autoPlay preload="none" src="/nh-media/cta-tech.mp4" />
        <div className="fx-cta-scrim" />
        <div className="fx-cta-inner">
          <h2 className="fx-reveal">Working in a vertical we haven't listed?</h2>
          <p className="fx-reveal">The hard parts — scale, compliance, reliability — travel well. Let's talk about yours.</p>
          <Link className="fx-btn fx-pop" to="/contact-us">Talk to Firebucks <span className="fx-arr">→</span></Link>
        </div>
      </section>
    </div>
  );
};

export default IndustriesLux;
